import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { User, Lock, History, Save, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, fetchUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [profileRes, activitiesRes] = await Promise.all([
        api.get('/profile'),
        api.get('/profile/activities'),
      ]);
      setProfile(profileRes.data.data || {});
      setActivities(activitiesRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const onSubmitProfile = async (data) => {
    try {
      const response = await api.put('/profile', data);
      setProfile(response.data.data);
      await fetchUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    }
  };

  const { register: registerPassword, handleSubmit: handlePasswordSubmit, reset: resetPassword, formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting } } = useForm();

  const onSubmitPassword = async (data) => {
    try {
      await api.post('/profile/change-password', {
        current_password: data.current_password,
        new_password: data.new_password,
        confirm_password: data.confirm_password,
      });
      toast.success('Password changed successfully');
      resetPassword();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">Profile</h1>
        <p className="page-subtitle">Manage your account settings and preferences</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'profile'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <User className="w-4 h-4 inline mr-2" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('password')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'password'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Lock className="w-4 h-4 inline mr-2" />
          Password
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'activity'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="w-4 h-4 inline mr-2" />
          Activity Log
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Info Card */}
          <div className="card lg:col-span-1">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <User className="w-12 h-12 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold">{profile?.full_name}</h2>
              <p className="text-gray-500">@{profile?.username}</p>
              <span className="badge badge-info mt-2 capitalize">{profile?.role}</span>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Email</span>
                <span className="font-medium">{profile?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mobile</span>
                <span className="font-medium">{profile?.mobile_number || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`badge ${profile?.status === 'approved' ? 'badge-success' : 'badge-warning'} capitalize`}>
                  {profile?.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Member Since</span>
                <span className="font-medium">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Edit Profile Form */}
          <div className="card lg:col-span-2">
            <h3 className="text-lg font-semibold mb-6">Edit Profile</h3>
            <form onSubmit={handleSubmit(onSubmitProfile)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Username *</label>
                  <input
                    type="text"
                    defaultValue={profile?.username}
                    {...register('username', { required: 'Username is required' })}
                    className={`input ${errors.username ? 'input-error' : ''}`}
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
                </div>
                <div>
                  <label className="label">Full Name *</label>
                  <input
                    type="text"
                    defaultValue={profile?.full_name}
                    {...register('full_name', { required: 'Full name is required' })}
                    className={`input ${errors.full_name ? 'input-error' : ''}`}
                  />
                </div>
                <div>
                  <label className="label">Email *</label>
                  <input
                    type="email"
                    defaultValue={profile?.email}
                    {...register('email', { required: 'Email is required' })}
                    className={`input ${errors.email ? 'input-error' : ''}`}
                  />
                </div>
                <div>
                  <label className="label">Mobile Number</label>
                  <input
                    type="tel"
                    defaultValue={profile?.mobile_number}
                    {...register('mobile_number')}
                    className="input"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                  <Save className="w-4 h-4" />
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="card max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold mb-6">Change Password</h3>
          <form onSubmit={handlePasswordSubmit(onSubmitPassword)} className="space-y-4">
            <div>
              <label className="label">Current Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...registerPassword('current_password', { required: 'Current password is required' })}
                  className={`input pr-10 ${passwordErrors.current_password ? 'input-error' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">New Password *</label>
              <input
                type={showPassword ? 'text' : 'password'}
                {...registerPassword('new_password', {
                  required: 'New password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                })}
                className={`input ${passwordErrors.new_password ? 'input-error' : ''}`}
              />
            </div>
            <div>
              <label className="label">Confirm New Password *</label>
              <input
                type={showPassword ? 'text' : 'password'}
                {...registerPassword('confirm_password', {
                  required: 'Please confirm your password',
                  validate: (value, formValues) => value === formValues.new_password || 'Passwords do not match',
                })}
                className={`input ${passwordErrors.confirm_password ? 'input-error' : ''}`}
              />
            </div>
            <div className="flex justify-end pt-4">
              <button type="submit" disabled={isPasswordSubmitting} className="btn btn-primary">
                <Lock className="w-4 h-4" />
                {isPasswordSubmitting ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          {activities.length > 0 ? (
            <div className="space-y-3">
              {activities.map((activity, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <History className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium">{activity.action}</p>
                    <p className="text-sm text-gray-500">{activity.details}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {activity.created_at ? new Date(activity.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500">No activity recorded yet</p>
          )}
        </div>
      )}
    </div>
  );
}
